import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TablePagination,
  Chip,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';

import EditStockModuleContext from 'contexts/EditStockModuleContext';
import { StockWarehouse } from 'types/models/StockReceipt';
import { getStockWarehouses } from 'services/stockWarehouseServices';
import { getSimpleVenuesWithStockModuleActive } from 'services/venuesServices';
import SelectField from 'components/SelectField';
import PaginationData from 'types/models/PaginationData';
import AppContext from 'contexts/AppContext';
import BooleanIcon from 'components/common/BooleanIcon';
import UserRoleType from 'types/enums/UserRoleType';
import PendingWarehouseMovements from 'components/Stock/PendingWarehouseMovements';
import { mapToSelectOptions } from 'utils/select';
import Venue from 'types/models/Venue';
import useSetupInitialState from 'hooks/useSetupInitialState';

const StockWarehouseListPage = (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();
  const match = useRouteMatch();
  const { setSelectedWarehouse } = useContext(EditStockModuleContext);

  const { authData, currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const isWarehouseEditor = authData?.user?.role === UserRoleType.WarehouseEditor;
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [venueId, setVenueId] = React.useState<string>('');
  const [paginationData, setPaginationData] = React.useState<PaginationData<StockWarehouse>>();

  React.useEffect(() => {
    let mounted = true;

    async function loadVenues() {
      if (isVenueEditor) {
        return;
      }
      try {
        const response = await getSimpleVenuesWithStockModuleActive();
        if (mounted) setVenues(response ?? []);
      } catch (error) {
        console.error(error);
      }
    }

    loadVenues();

    return () => {
      mounted = false;
    };
  }, [isVenueEditor]);

  React.useEffect(() => {
    if (!isVenueEditor) return;
    if (!entity?.id) return;
    setVenueId(entity.id);
  }, [isVenueEditor, entity]);

  const loadData = React.useCallback(
    async (page?: number, searchTerm?: string) => {
      try {
        const paginationData = await getStockWarehouses(
          page,
          searchTerm,
          isWarehouseEditor ? authData?.user?.id : undefined,
          venueId,
        );
        if (paginationData) setPaginationData(paginationData);
      } catch (error) {
        console.log(error);
      }
    },
    [isWarehouseEditor, authData?.user?.id, venueId],
  );

  React.useEffect(() => {
    if (!isWarehouseEditor && !venueId) {
      if (!isVenueEditor) {
        setPaginationData(undefined);
      }
      return;
    }
    if (currentPageListNumber) {
      loadData(currentPageListNumber);
    }
  }, [currentPageListNumber, loadData, isWarehouseEditor, isVenueEditor, venueId]);

  const myWarehouseId = isWarehouseEditor ? paginationData?.data?.[0]?.id : undefined;

  const handleEdit = (warehouse: StockWarehouse) => {
    setSelectedWarehouse(warehouse);
    history.push(`${match.url}/${warehouse.id}/edit`);
  };

  const renderContent = (paginationData: StockWarehouse[]): JSX.Element => {
    return (
      <>
        {paginationData.map((warehouse) => (
          <TableRow key={warehouse.id}>
            <TableCell>{warehouse.name}</TableCell>
            <TableCell>{warehouse.venue?.name || '-'}</TableCell>
            <TableCell>
              <Chip size="small" variant="outlined" label={t(`stock.warehouse-types.${warehouse.type}`)} />
            </TableCell>
            <TableCell>{warehouse.editor?.name || warehouse.editor?.email || '-'}</TableCell>
            <TableCell>
              <BooleanIcon value={warehouse.isActive} />
            </TableCell>
            <TableCell align="right">
              <Button onClick={() => handleEdit(warehouse)} variant="text" size="small">
                <EditIcon fontSize="small" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <>
      {!isVenueEditor && !isWarehouseEditor && (
        <SelectField
          name="venueId"
          label={t('venue')}
          placeholder={t('select')}
          value={venueId}
          options={mapToSelectOptions(venues, { label: 'name' })}
          setFieldValue={(_, value) => setVenueId(value as string)}
          touched={false}
          error={false}
          isMulti={false}
          isSearchable
          isClearable={false}
          backspaceRemovesValue={false}
          onBlur={() => undefined}
          isDisabled={false}
        />
      )}
      {isWarehouseEditor && myWarehouseId && <PendingWarehouseMovements warehouseId={myWarehouseId} />}

      {!isWarehouseEditor && (
        <Button
          className="btn-50 my-6"
          variant="contained"
          color="primary"
          onClick={() => {
            history.push('/private/stock/warehouses/create');
          }}
        >
          {t('stock.warehouses.create')}
        </Button>
      )}

      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('stock.warehouses.list.table.headers.name')}</TableCell>
              <TableCell>{t('stock.warehouses.list.table.headers.venue')}</TableCell>
              <TableCell>{t('stock.warehouses.list.table.headers.type')}</TableCell>
              <TableCell>{t('stock.warehouses.list.table.headers.manager')}</TableCell>
              <TableCell>{t('stock.warehouses.list.table.headers.active')}</TableCell>
              <TableCell align="right">{t('stock.warehouses.list.table.headers.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationData?.data && paginationData?.data.length > 0 ? (
              renderContent(paginationData.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('stock.warehouses.list.table.empty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationData && (
        <TablePagination
          component="div"
          count={paginationData.total}
          page={paginationData.currentPage - 1}
          onPageChange={(_, page: number) => {
            setCurrentPageListNumber(page + 1);
          }}
          rowsPerPage={paginationData.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </>
  );
};

export default StockWarehouseListPage;
